/**
 * VERBATIM port of merlin-core `integrations/shipStation/v2/rateShop/pickWinner.js` (2026-11-04).
 * Choose the winning rate from a quote, given settings + the rules plan. Pure function — no I/O.
 *
 * Candidate filter (in order):
 *   1. service must be in settings.comparableServices  (transit time is ignored by design)
 *   2. plan.onlyServices / plan.excludeServices
 *   3. plan.onlyCarriers / plan.excludeCarriers
 * Then:
 *   - plan.force wins if that service is present in the candidates (else fall back, noted)
 *   - plan.prefer entries win if within tolerance of the cheapest candidate
 *   - otherwise cheapest total; ties → fewer delivery days → stable code order
 */

function withinTolerance(candidate, cheapest, pref) {
    const diff = candidate.total - cheapest.total;
    if (diff <= 0) return true;
    const byAmount = pref.withinAmount > 0 && diff <= pref.withinAmount;
    const byPercent = pref.withinPercent > 0 && diff <= cheapest.total * (pref.withinPercent / 100);
    return byAmount || byPercent;
}

const sortRates = (rates) => [...rates].sort((a, b) =>
    a.total - b.total
    || (a.deliveryDays ?? 99) - (b.deliveryDays ?? 99)
    || `${a.carrierId}${a.serviceCode}`.localeCompare(`${b.carrierId}${b.serviceCode}`));

/**
 * @param {object} quote     result of quoteShipment()
 * @param {object} plan      from evaluateRules()
 * @param {object} settings  { comparableServices: string[] }
 * @returns {{ winner: object|null, candidates: object[], reason: string, notes: string[] }}
 */
export function pickWinner(quote, plan, settings) {
    const comparable = new Set(settings.comparableServices || []);
    const notes = [];
    let candidates = (quote.rates || []).filter((r) => comparable.has(r.serviceCode));
    if (plan.onlyServices) candidates = candidates.filter((r) => plan.onlyServices.has(r.serviceCode));
    candidates = candidates.filter((r) => !plan.excludeServices.has(r.serviceCode));
    if (plan.onlyCarriers) candidates = candidates.filter((r) => plan.onlyCarriers.has(r.carrierId));
    candidates = candidates.filter((r) => !plan.excludeCarriers.has(r.carrierId));
    candidates = sortRates(candidates);

    if (!candidates.length) return { winner: null, candidates, reason: "no comparable rates after filters", notes };

    if (plan.force) {
        const forced = candidates.find((r) => r.serviceCode === plan.force.serviceCode && (!plan.force.carrierId || r.carrierId === plan.force.carrierId));
        if (forced) return { winner: forced, candidates, reason: `forced by rule ${plan.force.ruleId}`, notes };
        notes.push(`force_service ${plan.force.serviceCode} (rule ${plan.force.ruleId}) not available — fell back`);
    }

    const cheapest = candidates[0];
    for (const pref of plan.prefer) {
        const match = candidates.find((r) => r.serviceCode === pref.serviceCode && (!pref.carrierId || r.carrierId === pref.carrierId));
        if (match && withinTolerance(match, cheapest, pref)) {
            return { winner: match, candidates, reason: `preferred by rule ${pref.ruleId} (within tolerance of $${cheapest.total.toFixed(2)})`, notes };
        }
    }
    return { winner: cheapest, candidates, reason: "cheapest comparable", notes };
}

/** Savings of the winner vs. the runner-up and vs. a currently-assigned service, if rated. */
export function savings(winner, candidates, currentRate) {
    if (!winner) return { vsNextBest: null, vsCurrent: null };
    const next = candidates.find((r) => r !== winner);
    return {
        vsNextBest: next ? Number((next.total - winner.total).toFixed(2)) : null,
        vsCurrent: currentRate ? Number((currentRate.total - winner.total).toFixed(2)) : null,
    };
}
